/**
 * Dashboard Page — Indicators Module
 * 
 * EXPORT STRATEGY (IMPORTANTE):
 * =============================
 * 
 * Este arquivo é a PÁGINA principal do módulo Indicators (Dashboard).
 * 
 * PUBLIC EXPORTS (re-exporte de componentes reutilizáveis):
 *   - NENHUM: A página Dashboard é exportada como default, componentes internos não são re-exportados
 * 
 * PRIVATE COMPONENTS (importados diretamente onde necessário):
 *   - IndicatorCard: USE ONLY within this Dashboard page ✅
 *   - FilterPanel: USE ONLY within this Dashboard page ✅
 *   - TimeSeriesChart: USE ONLY within this Dashboard page ✅
 *   - TopContactsChart: USE ONLY within this Dashboard page ✅
 *   - CardCounter: PRIVATE — imported directly by CampaignReport from ../Indicators/components/CardCounter ✅
 *   - TableAttendantsStatus: PRIVATE — imported directly by Dashboard from ../Indicators/components/TableAttendantsStatus ✅
 * 
 * UNUSED COMPONENTS:
 *   - DashboardCard.tsx: Componente órfão (não utilizado em lugar nenhum) — CANDIDATO PARA REMOÇÃO
 * 
 * IMPORT VALIDATION SUMMARY (Subtask indicators-fix-003):
 * ======================================================
 * ✅ AC1. Todos os imports em index.tsx validados:
 *    - IndicatorCard (./components/IndicatorCard) ✅
 *    - FilterPanel (./components/FilterPanel) ✅
 *    - TimeSeriesChart (./components/TimeSeriesChart) ✅
 *    - TopContactsChart (./components/TopContactsChart) ✅
 *    - useDashboardData (../../hooks/useDashboardData) ✅
 * 
 * ✅ AC2. Documentação JSDoc — export strategy clara e documentada
 * 
 * ✅ AC3. Nenhum path quebrado, nenhum circular import detectado
 *    - CardCounter não importa de volta para Indicators/index.tsx
 *    - TableAttendantsStatus importa de ../../../translate/i18n (módulo compartilhado) ✅
 * 
 * ✅ AC4. Export strategy clara:
 *    - CardCounter e TableAttendantsStatus = PRIVATE (direct import)
 *    - IndicatorCard, FilterPanel, etc = PRIVATE (internal to Dashboard)
 *    - Dashboard componente = DEFAULT EXPORT (página principal)
 * 
 * ✅ AC5. Imports em CampaignReport e Dashboard validados:
 *    - CampaignReport: `import CardCounter from "../Indicators/components/CardCounter"` ✅
 *    - Dashboard: `import TableAttendantsStatus from "../Indicators/components/TableAttendantsStatus"` ✅
 * 
 * RATIONALE:
 *   - Dashboard page maintains separation of concerns
 *   - Child components are internal implementation details
 *   - External packages (CampaignReport, Dashboard) import specific components directly
 *   - Avoids coupling and circular import risks
 * 
 * @module Indicators/Dashboard
 * @component
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
// Internal imports: IndicatorCard, FilterPanel, TimeSeriesChart, TopContactsChart
// ✅ VALIDATED: All paths are relative and correct (./components/ComponentName)
// ✅ NO CIRCULAR IMPORTS: Components do not import back from index.tsx
import IndicatorCard from "./components/IndicatorCard";
import FilterPanel from "./components/FilterPanel";
import TimeSeriesChart from "./components/TimeSeriesChart";
import TopContactsChart from "./components/TopContactsChart";
import { useDashboardData } from "../../hooks/useDashboardData";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  container: {
    maxWidth: "1400px",
  },
  header: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  cardSection: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  chartsGrid: {
    marginTop: theme.spacing(3),
  },
}));

interface DashboardProps {
  onFilterChange?: (filters: any) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onFilterChange }) => {
  const classes = useStyles();
  const {
    data,
    loading,
    error,
    filters,
    updateFilters,
    refreshData,
  } = useDashboardData();

  const handleFilterChange = (newFilters: any) => {
    updateFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  if (error) {
    return (
      <Container className={classes.root}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (loading || !data) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const { indicators, topContactsByTickets, topContactsByMessages, timeSeries } =
    data;

  return (
    <Container maxWidth="lg" className={classes.root}>
      {/* Header */}
      <Typography variant="h3" className={classes.header}>
        📊 Dashboard de Indicadores
      </Typography>

      {/* Filtros */}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onRefresh={refreshData}
      />

      {/* Indicadores Principais */}
      <Box className={classes.cardSection}>
        <Typography variant="h5" gutterBottom>
          Indicadores Principais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <IndicatorCard
              title="Tickets Abertos"
              value={indicators?.openTickets || 0}
              color="#4CAF50"
              icon="📋"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <IndicatorCard
              title="Tickets Encerrados"
              value={indicators?.closedTickets || 0}
              color="#2196F3"
              icon="✅"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <IndicatorCard
              title="Tickets Pendentes"
              value={indicators?.pendingTickets || 0}
              color="#FF9800"
              icon="⏳"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <IndicatorCard
              title="Total de Mensagens"
              value={indicators?.totalMessages || 0}
              color="#9C27B0"
              icon="💬"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <IndicatorCard
              title="Contatos Únicos"
              value={indicators?.uniqueContacts || 0}
              color="#F44336"
              icon="👥"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Série Temporal */}
      <Box className={classes.cardSection}>
        <Typography variant="h5" gutterBottom>
          Evolução no Tempo
        </Typography>
        <Card>
          <CardContent>
            {timeSeries && timeSeries.length > 0 ? (
              <TimeSeriesChart data={timeSeries} groupBy={filters.groupBy} />
            ) : (
              <Alert severity="info">Sem dados disponíveis para este período</Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Rankings */}
      <Grid container spacing={3} className={classes.chartsGrid}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🏆 Top 10 Contatos por Tickets
              </Typography>
              {topContactsByTickets && topContactsByTickets.length > 0 ? (
                <TopContactsChart
                  data={topContactsByTickets}
                  metric="Tickets"
                />
              ) : (
                <Alert severity="info">Sem dados disponíveis</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🏆 Top 10 Contatos por Mensagens
              </Typography>
              {topContactsByMessages && topContactsByMessages.length > 0 ? (
                <TopContactsChart
                  data={topContactsByMessages}
                  metric="Mensagens"
                />
              ) : (
                <Alert severity="info">Sem dados disponíveis</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
